import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import employeeAttendanceHrAPI from '../../api/employeeAttendanceHrAPI';
import {
  fetchWithHrToken,
  HR_SESSION_ERROR,
  localInputToUtc,
  utcToLocalInput,
} from '../../utils/hrApiHelpers';

export default function HrAttendanceEditModal({
  ensureHrToken,
  mode, // 'edit' | 'create'
  attendanceId,
  employeeId,
  employeeName,
  date,
  initialCheckIn,
  initialCheckOut,
  onClose,
  onSaved,
}) {
  const [checkIn, setCheckIn] = useState(utcToLocalInput(initialCheckIn));
  const [checkOut, setCheckOut] = useState(utcToLocalInput(initialCheckOut));
  const [clearIn, setClearIn] = useState(false);
  const [clearOut, setClearOut] = useState(false);
  const [createDate, setCreateDate] = useState(date || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    let result;
    if (mode === 'create') {
      if (!createDate) {
        setError('Date is required.');
        setSaving(false);
        return;
      }
      const params = {
        employee_id: Number(employeeId),
        date: createDate,
      };
      const inUtc = localInputToUtc(checkIn);
      const outUtc = localInputToUtc(checkOut);
      if (inUtc) params.check_in = inUtc;
      if (outUtc) params.check_out = outUtc;

      result = await fetchWithHrToken(ensureHrToken, (token) =>
        employeeAttendanceHrAPI.createAttendance(params, token)
      );
    } else {
      const params = { attendance_id: Number(attendanceId) };
      if (clearIn) {
        params.clear_check_in = true;
      } else if (checkIn) {
        params.check_in = localInputToUtc(checkIn);
      }
      if (clearOut) {
        params.clear_check_out = true;
      } else if (checkOut) {
        params.check_out = localInputToUtc(checkOut);
      }

      result = await fetchWithHrToken(ensureHrToken, (token) =>
        employeeAttendanceHrAPI.updateAttendance(params, token)
      );
    }

    setSaving(false);

    if (result.error === 'no_token') {
      setError(HR_SESSION_ERROR);
      return;
    }
    if (result.error) {
      if (result.code === 409) {
        setError(`${result.error} Use edit on the existing day instead.`);
      } else {
        setError(result.error);
      }
      return;
    }

    onSaved(result.data);
  };

  return createPortal(
    <div className="ea-dash-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="ea-dash-modal ea-hr-form-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ea-hr-att-edit-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="ea-dash-modal__close" onClick={onClose} aria-label="Close">
          ×
        </button>
        <h3 id="ea-hr-att-edit-title" className="ea-dash-modal__title">
          {mode === 'create' ? 'Create attendance day' : 'Edit attendance'}
        </h3>
        <p className="ea-dash-modal__subtitle">
          {employeeName || 'Employee'}
          {date ? ` · ${date}` : ''}
        </p>
        <p className="ea-hr-form-modal__hint">
          Times are entered in your local timezone and sent to the API as UTC.
        </p>

        <form className="ea-hr-form" onSubmit={handleSubmit}>
          {error && (
            <div className="ea-toast error show ea-dash-error" role="alert">
              {error}
            </div>
          )}
          <div className="ea-hr-form__grid ea-hr-form__grid--single">
            {mode === 'create' && (
              <label>
                <span className="ea-field-label">Date *</span>
                <input
                  type="date"
                  className="ea-field-input"
                  value={createDate}
                  onChange={(e) => setCreateDate(e.target.value)}
                  required
                />
              </label>
            )}
            <label>
              <span className="ea-field-label">Check-in (local)</span>
              <input
                type="datetime-local"
                className="ea-field-input"
                value={checkIn}
                onChange={(e) => {
                  setCheckIn(e.target.value);
                  setClearIn(false);
                }}
                disabled={clearIn}
              />
            </label>
            {mode === 'edit' && (
              <label className="ea-hr-form__check">
                <input
                  type="checkbox"
                  checked={clearIn}
                  onChange={(e) => setClearIn(e.target.checked)}
                />
                <span>Clear check-in</span>
              </label>
            )}
            <label>
              <span className="ea-field-label">Check-out (local)</span>
              <input
                type="datetime-local"
                className="ea-field-input"
                value={checkOut}
                onChange={(e) => {
                  setCheckOut(e.target.value);
                  setClearOut(false);
                }}
                disabled={clearOut}
              />
            </label>
            {mode === 'edit' && (
              <label className="ea-hr-form__check">
                <input
                  type="checkbox"
                  checked={clearOut}
                  onChange={(e) => setClearOut(e.target.checked)}
                />
                <span>Clear check-out</span>
              </label>
            )}
          </div>
          <div className="ea-hr-form__actions">
            <button type="button" className="ea-hr-btn ea-hr-btn--ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="ea-btn-submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
